"use client";

import Image from "next/image";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { readAllOverrides, readOverrideForSlug, type StoreOverride } from "@/hooks/use-store-overrides";

interface Props {
  name: string;
  storeId?: string;
  logo?: string | null;
  size?: number; // Size sets a square unless overriden by className
  className?: string;
  rounded?: string;
}

function initials(name: string) {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? "").join("") || "?";
}

function colorFromName(name: string) {
  const palette = [
    "from-sky-500 to-blue-600",
    "from-fuchsia-500 to-pink-600",
    "from-amber-500 to-orange-600",
    "from-emerald-500 to-green-600",
    "from-violet-500 to-indigo-600",
    "from-rose-500 to-red-600",
    "from-teal-500 to-cyan-600",
    "from-lime-500 to-emerald-600",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return palette[Math.abs(hash) % palette.length];
}

export function StoreLogo({ name, storeId, logo: initialLogo, size, className, rounded = "[8px]" }: Props) {
  const [errored, setErrored] = useState(false);
  const [overrideLogo, setOverrideLogo] = useState<string | null>(null);

  useEffect(() => {
    let match: StoreOverride | null = null;
    if (storeId) {
      match = readOverrideForSlug(storeId);
    } else {
      const all = readAllOverrides();
      match = Object.values(all).find((o) => o.name && o.name.toLowerCase() === name.toLowerCase()) || null;
    }
    if (match && (match.logoVisual || match.logo)) {
      setOverrideLogo(match.logoVisual || match.logo || null);
    }
  }, [name, storeId]);

  const activeLogo = overrideLogo || initialLogo;
  const showLogo = !!activeLogo && !errored;
  const radius = rounded.startsWith("[") ? `rounded-${rounded}` : rounded === "full" ? "rounded-full" : `rounded-${rounded}`;

  return (
    <div
      style={size ? { width: size, height: size } : undefined}
      className={cn(
        radius,
        "bg-white flex items-center justify-center overflow-hidden shrink-0 relative",
        className
      )}
    >
      {showLogo ? (
        <div className="relative w-full h-full p-1.5 flex items-center justify-center">
          <Image
            src={activeLogo!}
            alt={name}
            fill
            unoptimized
            onError={() => setErrored(true)}
            className="object-contain"
          />
        </div>
      ) : (
        <div
          className={cn(
            "w-full h-full flex items-center justify-center text-white font-black",
            `bg-linear-to-br ${colorFromName(name)}`
          )}
          style={{ fontSize: size ? size * 0.38 : "1rem" }}
        >
          {initials(name)}
        </div>
      )}
    </div>
  );
}
